import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface StoryWithImages {
  id: string;
  title: any;
  cover_url: string | null;
  link_url: string | null;
  display_order: number;
  story_images: { url: string; position: number; caption: any }[];
  // Property metadata used by the viewer
  property_id?: string | null;
  price?: number | null;
  currency?: string | null;
  listing_type?: string | null;
  property_type?: string | null;
  city?: string | null;
  sub_city?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  area_sqm?: number | null;
  area_unit?: string | null;
  description?: any;
  reference_code?: string | null;
  slug?: string | null;
  contact_phone?: string | null;
  contact_person?: string | null;
  agent_id?: string | null;
  agency_id?: string | null;
  agency_name?: string | null;
  agency_logo_url?: string | null;
}

export function useStories() {
  return useQuery({
    queryKey: ['landing-stories-curated'],
    queryFn: async (): Promise<StoryWithImages[]> => {
      const nowIso = new Date().toISOString();
      const { data, error } = await (supabase as any)
        .from('stories')
        .select(`
          id, title, cover_url, link_url, display_order, property_id,
          starts_at, ends_at, is_active,
          story_images(url, position, caption),
          properties!inner(
            id, title, description, price, currency, listing_type, property_type,
            city, sub_city, bedrooms, bathrooms, area_sqm, user_id,
            reference_code, slug, contact_phone, contact_person, status, approval_status,
            property_images(url, sort_order, is_primary)
          )
        `)
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .limit(20);
      if (error) throw error;

      const PLACEHOLDER = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop';

      const filtered = ((data || []) as any[]).filter((s: any) => {
        if (s.starts_at && new Date(s.starts_at) > new Date()) return false;
        if (s.ends_at && new Date(s.ends_at) <= new Date()) return false;
        const p = s.properties;
        return p && p.status === 'active' && p.approval_status === 'approved';
      });

      // Fetch agency info for each story's property owner
      const userIds = Array.from(new Set(filtered.map((s: any) => s.properties?.user_id).filter(Boolean)));
      let agencyByUser: Record<string, { id: string; name: string; logo_url: string | null }> = {};
      if (userIds.length > 0) {
        const { data: aps } = await (supabase as any)
          .from('agent_profiles')
          .select('user_id, agencies(id, name, logo_url)')
          .in('user_id', userIds);
        for (const ap of (aps || [])) {
          if (ap.agencies) agencyByUser[ap.user_id] = ap.agencies;
        }
      }

      return filtered.map((s: any): StoryWithImages => {
        const p = s.properties;
        const propImgs = (p.property_images || [])
          .slice()
          .sort((a: any, b: any) => {
            if (a.is_primary && !b.is_primary) return -1;
            if (!a.is_primary && b.is_primary) return 1;
            return (a.sort_order ?? 0) - (b.sort_order ?? 0);
          });
        const customSlides = (s.story_images || [])
          .slice()
          .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));

        const slidesSource = customSlides.length > 0
          ? customSlides.map((i: any, idx: number) => ({ url: i.url, position: idx, caption: i.caption ?? null }))
          : propImgs.length > 0
            ? propImgs.slice(0, 8).map((i: any, idx: number) => ({ url: i.url, position: idx, caption: null }))
            : [{ url: PLACEHOLDER, position: 0, caption: null }];

        const cover = s.cover_url || slidesSource[0]?.url || PLACEHOLDER;
        const agency = agencyByUser[p.user_id];

        return {
          id: s.id,
          title: s.title || p.title,
          cover_url: cover,
          link_url: s.link_url || `/property/${p.slug || p.id}`,
          display_order: s.display_order ?? 0,
          story_images: slidesSource,
          property_id: p.id,
          price: p.price,
          currency: p.currency,
          listing_type: p.listing_type,
          property_type: p.property_type,
          city: p.city,
          sub_city: p.sub_city,
          bedrooms: p.bedrooms,
          bathrooms: p.bathrooms,
          area_sqm: p.area_sqm,
          area_unit: 'm²',
          description: p.description,
          reference_code: p.reference_code,
          slug: p.slug,
          contact_phone: p.contact_phone,
          contact_person: p.contact_person,
          agent_id: null,
          agency_id: agency?.id ?? null,
          agency_name: agency?.name ?? null,
          agency_logo_url: agency?.logo_url ?? null,
        };
      });
    },
    staleTime: 2 * 60 * 1000,
  });
}
